import React, { useState, useEffect, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import type { SystemSettings, Holiday } from '@lds-efsp/shared-contracts';
import {
  SetupDetailDrawer,
  StatusBadge,
} from '../components';
import FormInput, { FormSelect, FormTextarea, FormCheckbox } from '../components/FormInput';
import SystemTab from '../components/SystemTab';
import { useApi } from '../../../api/useApi';

interface SetupSettingsProps {
  brand: BrandConfig;
  Icons: IconsType;
}

type SettingsTab = 'general' | 'holidays' | 'quickbooks' | 'system';

const holidayTypeOptions = [
  { value: 'federal', label: 'Federal' },
  { value: 'state', label: 'State' },
  { value: 'company', label: 'Company' },
  { value: 'court-specific', label: 'Court Specific' },
];

const SetupSettings: React.FC<SetupSettingsProps> = ({ brand, Icons }) => {
  const { get, post, patch, del } = useApi();
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<SystemSettings>>({});

  // Holiday modal state
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
  const [isHolidayDrawerOpen, setIsHolidayDrawerOpen] = useState(false);
  const [isCreatingHoliday, setIsCreatingHoliday] = useState(false);
  const [holidayFormData, setHolidayFormData] = useState<Partial<Holiday>>({});

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await get<SystemSettings>('/api/admin/setup/settings');
      setSettings(data);
      setFormData(data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [get]);

  const fetchHolidays = useCallback(async () => {
    try {
      const data = await get<{ data: Holiday[] }>('/api/admin/setup/holidays');
      setHolidays(data.data || []);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    }
  }, [get]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchSettings();
    fetchHolidays();
  }, [fetchSettings, fetchHolidays]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name.startsWith('companyAddress.')) {
      const field = name.replace('companyAddress.', '');
      setFormData(prev => ({
        ...prev,
        companyAddress: {
          ...prev.companyAddress,
          street1: prev.companyAddress?.street1 || '',
          city: prev.companyAddress?.city || '',
          state: prev.companyAddress?.state || '',
          zip: prev.companyAddress?.zip || '',
          country: prev.companyAddress?.country || 'US',
          [field]: value,
        },
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await patch('/api/admin/setup/settings', formData);
      fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Holiday handlers
  const handleAddHoliday = () => {
    setSelectedHoliday(null);
    setHolidayFormData({
      name: '',
      date: '',
      holidayType: 'federal',
      recurring: false,
    });
    setIsCreatingHoliday(true);
    setIsHolidayDrawerOpen(true);
  };

  const handleEditHoliday = (holiday: Holiday) => {
    setSelectedHoliday(holiday);
    setHolidayFormData(holiday);
    setIsCreatingHoliday(false);
    setIsHolidayDrawerOpen(true);
  };

  const handleHolidayInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === 'checkbox') {
      setHolidayFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setHolidayFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveHoliday = async () => {
    setIsSaving(true);
    try {
      if (isCreatingHoliday) {
        await post('/api/admin/setup/holidays', holidayFormData);
      } else {
        await patch(`/api/admin/setup/holidays/${selectedHoliday?.id}`, holidayFormData);
      }

      setIsHolidayDrawerOpen(false);
      fetchHolidays();
    } catch (error) {
      console.error('Failed to save holiday:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteHoliday = async (id: string) => {
    if (!confirm('Are you sure you want to delete this holiday?')) return;

    try {
      await del(`/api/admin/setup/holidays/${id}`);
      fetchHolidays();
    } catch (error) {
      console.error('Failed to delete holiday:', error);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const tabs: { key: SettingsTab; label: string }[] = [
    { key: 'general', label: 'General' },
    { key: 'holidays', label: 'Holidays' },
    { key: 'quickbooks', label: 'QuickBooks' },
    { key: 'system', label: 'System' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: brand.colors.primary, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Tab Navigation */}
      <div
        className="flex border-b mb-6"
        style={{ borderColor: brand.colors.border }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`settings-tab-${tab.key}`}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.key ? '' : 'border-transparent'
            }`}
            style={{
              color: activeTab === tab.key ? brand.colors.link : brand.colors.textLight,
              borderColor: activeTab === tab.key ? brand.colors.primary : 'transparent',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div
          className="bg-white rounded-lg border p-6"
          style={{ borderColor: brand.colors.border }}
        >
          <div className="max-w-2xl space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-4" style={{ color: brand.colors.secondary }}>
                Company Information
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    brand={brand}
                    label="Company Name"
                    name="companyName"
                    value={formData.companyName || ''}
                    onChange={handleInputChange}
                    required
                  />
                  <FormInput
                    brand={brand}
                    label="Legal Name"
                    name="companyLegalName"
                    value={formData.companyLegalName || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <FormInput
                  brand={brand}
                  label="Street Address"
                  name="companyAddress.street1"
                  value={formData.companyAddress?.street1 || ''}
                  onChange={handleInputChange}
                />
                <div className="grid grid-cols-3 gap-4">
                  <FormInput
                    brand={brand}
                    label="City"
                    name="companyAddress.city"
                    value={formData.companyAddress?.city || ''}
                    onChange={handleInputChange}
                  />
                  <FormInput
                    brand={brand}
                    label="State"
                    name="companyAddress.state"
                    value={formData.companyAddress?.state || ''}
                    onChange={handleInputChange}
                  />
                  <FormInput
                    brand={brand}
                    label="ZIP"
                    name="companyAddress.zip"
                    value={formData.companyAddress?.zip || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    brand={brand}
                    label="Phone"
                    name="companyPhone"
                    type="tel"
                    value={formData.companyPhone || ''}
                    onChange={handleInputChange}
                  />
                  <FormInput
                    brand={brand}
                    label="Fax"
                    name="companyFax"
                    type="tel"
                    value={formData.companyFax || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    brand={brand}
                    label="Email"
                    name="companyEmail"
                    type="email"
                    value={formData.companyEmail || ''}
                    onChange={handleInputChange}
                  />
                  <FormInput
                    brand={brand}
                    label="Website"
                    name="companyWebsite"
                    type="url"
                    value={formData.companyWebsite || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-6" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-lg font-medium mb-4" style={{ color: brand.colors.secondary }}>
                Fee Settings
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  brand={brand}
                  label="Credit Card Fee (%)"
                  name="creditCardFeePercent"
                  type="number"
                  value={formData.creditCardFeePercent || 0}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="ACH Fee ($)"
                  name="achFeeFlat"
                  type="number"
                  value={formData.achFeeFlat || 0}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="Rush Fee Multiplier"
                  name="rushFeeMultiplier"
                  type="number"
                  value={formData.rushFeeMultiplier || 1.5}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="Same-Day Fee Multiplier"
                  name="sameDayFeeMultiplier"
                  type="number"
                  value={formData.sameDayFeeMultiplier || 2.0}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="border-t pt-6" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-lg font-medium mb-4" style={{ color: brand.colors.secondary }}>
                SLA Defaults (Hours)
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <FormInput
                  brand={brand}
                  label="Standard"
                  name="defaultSlaStandard"
                  type="number"
                  value={formData.defaultSlaStandard || 24}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="Priority"
                  name="defaultSlaPriority"
                  type="number"
                  value={formData.defaultSlaPriority || 8}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="Urgent"
                  name="defaultSlaUrgent"
                  type="number"
                  value={formData.defaultSlaUrgent || 4}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="border-t pt-6" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-lg font-medium mb-4" style={{ color: brand.colors.secondary }}>
                Default Messages
              </h3>
              <FormTextarea
                brand={brand}
                label="Order Confirmation Message"
                name="orderConfirmationMessage"
                value={formData.orderConfirmationMessage || ''}
                onChange={handleInputChange}
                rows={2}
              />
              <FormTextarea
                brand={brand}
                label="Order Completion Message"
                name="orderCompletionMessage"
                value={formData.orderCompletionMessage || ''}
                onChange={handleInputChange}
                rows={2}
              />
              <FormTextarea
                brand={brand}
                label="Invoice Message"
                name="invoiceMessage"
                value={formData.invoiceMessage || ''}
                onChange={handleInputChange}
                rows={2}
              />
            </div>

            <div className="border-t pt-6" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-lg font-medium mb-4" style={{ color: brand.colors.secondary }}>
                Feature Flags
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <FormCheckbox
                  brand={brand}
                  label="Retainer Enabled"
                  name="retainerEnabled"
                  checked={formData.retainerEnabled || false}
                  onChange={handleInputChange}
                />
                <FormCheckbox
                  brand={brand}
                  label="Pre-Auth Enabled"
                  name="preAuthEnabled"
                  checked={formData.preAuthEnabled || false}
                  onChange={handleInputChange}
                />
                <FormCheckbox
                  brand={brand}
                  label="E-Filing Enabled"
                  name="efilingEnabled"
                  checked={formData.efilingEnabled || false}
                  onChange={handleInputChange}
                />
                <FormCheckbox
                  brand={brand}
                  label="E-Service Enabled"
                  name="eserviceEnabled"
                  checked={formData.eserviceEnabled || false}
                  onChange={handleInputChange}
                />
                <FormCheckbox
                  brand={brand}
                  label="Vendor Dispatch Enabled"
                  name="vendorDispatchEnabled"
                  checked={formData.vendorDispatchEnabled || false}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={handleSaveSettings}
                disabled={isSaving}
                className="px-6 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
              >
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Holidays Tab */}
      {activeTab === 'holidays' && (
        <div
          className="bg-white rounded-lg border"
          style={{ borderColor: brand.colors.border }}
        >
          <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: brand.colors.border }}>
            <h3 className="text-lg font-medium" style={{ color: brand.colors.secondary }}>
              Holiday Calendar
            </h3>
            <button
              onClick={handleAddHoliday}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              <Icons.Plus className="w-4 h-4" />
              Add Holiday
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: brand.colors.bgLight }}>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Recurring</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Notes</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: brand.colors.border }}>
                {holidays.map(holiday => (
                  <tr key={holiday.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm" style={{ color: brand.colors.text }}>
                      {formatDate(holiday.date)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: brand.colors.text }}>
                      {holiday.name}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={holiday.holidayType} size="sm" />
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: brand.colors.textLight }}>
                      {holiday.recurring ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: brand.colors.textLight }}>
                      {holiday.notes || '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleEditHoliday(holiday)}
                        className="text-sm font-medium mr-3"
                        style={{ color: brand.colors.link }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteHoliday(holiday.id)}
                        className="text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {holidays.length === 0 && (
            <div className="p-8 text-center" style={{ color: brand.colors.textLight }}>
              No holidays configured. Click "Add Holiday" to create one.
            </div>
          )}
        </div>
      )}

      {/* QuickBooks Tab */}
      {activeTab === 'quickbooks' && (
        <div
          className="bg-white rounded-lg border p-6"
          style={{ borderColor: brand.colors.border }}
        >
          <div className="max-w-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#2CA01C' }}
              >
                <span className="text-white text-xl font-bold">QB</span>
              </div>
              <div>
                <h3 className="text-lg font-medium" style={{ color: brand.colors.secondary }}>
                  QuickBooks Integration
                </h3>
                <p className="text-sm mt-1" style={{ color: brand.colors.textLight }}>
                  Connect your QuickBooks account to sync invoices, payments, and customer data.
                </p>
              </div>
            </div>

            <div
              className="p-4 rounded-lg mb-6"
              style={{ backgroundColor: brand.colors.bgLight }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium" style={{ color: brand.colors.text }}>
                    Connection Status
                  </div>
                  <div className="mt-1">
                    <StatusBadge status={settings?.quickbooks?.connectionStatus || 'disconnected'} />
                  </div>
                </div>
                <button
                  className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-white"
                  style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                  disabled
                >
                  Connect QuickBooks
                </button>
              </div>
            </div>

            <div
              className="p-4 rounded-lg border"
              style={{ borderColor: '#FDE68A', backgroundColor: '#FFFBEB' }}
            >
              <div className="flex gap-3">
                <Icons.AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#92400E' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: '#92400E' }}>
                    Coming Soon
                  </p>
                  <p className="text-sm mt-1" style={{ color: '#B45309' }}>
                    QuickBooks integration is under development. This feature will allow you to automatically sync invoices, payments, and customer records between this platform and your QuickBooks account.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                Planned Features
              </h4>
              <ul className="space-y-2 text-sm" style={{ color: brand.colors.textLight }}>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: brand.colors.primary }} />
                  Automatic invoice sync when orders are completed
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: brand.colors.primary }} />
                  Payment recording and reconciliation
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: brand.colors.primary }} />
                  Customer data synchronization
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: brand.colors.primary }} />
                  Configurable sync frequency (real-time, hourly, daily)
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* System Tab */}
      {activeTab === 'system' && (
        <SystemTab brand={brand} Icons={Icons} />
      )}

      {/* Holiday Edit Drawer */}
      <SetupDetailDrawer
        isOpen={isHolidayDrawerOpen}
        onClose={() => setIsHolidayDrawerOpen(false)}
        title={isCreatingHoliday ? 'Add Holiday' : 'Edit Holiday'}
        subtitle={selectedHoliday?.name}
        brand={brand}
        Icons={Icons}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsHolidayDrawerOpen(false)}
              className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveHoliday}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <FormInput
            brand={brand}
            label="Holiday Name"
            name="name"
            value={holidayFormData.name || ''}
            onChange={handleHolidayInputChange}
            required
          />
          <FormInput
            brand={brand}
            label="Date"
            name="date"
            type="date"
            value={holidayFormData.date || ''}
            onChange={handleHolidayInputChange}
            required
          />
          <FormSelect
            brand={brand}
            label="Type"
            name="holidayType"
            value={holidayFormData.holidayType || 'federal'}
            onChange={handleHolidayInputChange}
            options={holidayTypeOptions}
          />
          {(holidayFormData.holidayType === 'state' || holidayFormData.holidayType === 'court-specific') && (
            <FormInput
              brand={brand}
              label={holidayFormData.holidayType === 'state' ? 'State' : 'Court Code'}
              name={holidayFormData.holidayType === 'state' ? 'state' : 'courtCode'}
              value={holidayFormData.holidayType === 'state' ? holidayFormData.state || '' : holidayFormData.courtCode || ''}
              onChange={handleHolidayInputChange}
            />
          )}
          <FormCheckbox
            brand={brand}
            label="Recurring Annually"
            name="recurring"
            checked={holidayFormData.recurring || false}
            onChange={handleHolidayInputChange}
          />
          <FormTextarea
            brand={brand}
            label="Notes"
            name="notes"
            value={holidayFormData.notes || ''}
            onChange={handleHolidayInputChange}
            rows={2}
          />
        </div>
      </SetupDetailDrawer>
    </div>
  );
};

export default SetupSettings;
